import {
  createColDefCheckmark,
  ItemGrid as ItemGridGeneric,
  type ItemGridPaginationProps,
  type ItemGridSortOrder,
} from "@package/ui-apollo-components";
import { type GetEventsQuery } from "@package/ui-gql/index-analytics";
import type {
  GetLayoutGridQuery,
  GetLocationGridQuery,
  GetMediaGridQuery,
  GetPostGridQuery,
  GetProfileGridQuery,
  GetServiceGridQuery,
  GetTagGridQuery,
} from "@package/ui-gql/index-data-access";
import { type AnyApiGridQueryStateValue } from "@package/ui-redux";
import { type ColDef } from "ag-grid-community";
import { type ReactNode, useCallback, useMemo } from "react";
import {
  setItemGridFilter,
  setItemGridFilterOpen,
  setItemGridPage,
  setItemGridPerPage,
  setItemGridQuery,
  setItemGridSelectedItemId,
  setItemGridSortField,
  setItemGridSortOrder,
  setItemGridWithDeleted,
  type SliceItemGridState,
} from "../../store/slices/slice-item-grid";
import { useAppDispatch, useAppSelector } from "../../store/store";

type AllItemsGridQuery =
  | GetEventsQuery
  | GetLayoutGridQuery
  | GetLocationGridQuery
  | GetMediaGridQuery
  | GetPostGridQuery
  | GetProfileGridQuery
  | GetServiceGridQuery
  | GetTagGridQuery;

type ItemGridDataRow = AllItemsGridQuery["items"][0];

export type ItemGridProps = {
  columnDefs: ColDef[];
  sidebarEntryPath: keyof SliceItemGridState;
  data?: AllItemsGridQuery;
  loading?: boolean;
  error?: boolean;
  filterPanels?: ReactNode[];
  filtersSupported?: boolean;
  pagination?: Partial<ItemGridPaginationProps>;
};

export function ItemGrid({
  columnDefs,
  sidebarEntryPath,
  data,
  error,
  loading,
  filterPanels,
  filtersSupported = false,
  pagination,
}: ItemGridProps) {
  const dispatch = useAppDispatch();

  const filtersOpen = useAppSelector(
    (state) => state.itemGrid[sidebarEntryPath].filtersOpen,
  );
  const filter = useAppSelector(
    (state) => state.itemGrid[sidebarEntryPath].variables.filter,
  );
  const page = useAppSelector(
    (state) => state.itemGrid[sidebarEntryPath].variables.page ?? 0,
  );
  const perPage = useAppSelector(
    (state) => state.itemGrid[sidebarEntryPath].variables.perPage ?? 10,
  );
  const q = useAppSelector(
    (state) => state.itemGrid[sidebarEntryPath].variables.filter?.q ?? "",
  );
  const withDeleted = useAppSelector(
    (state) =>
      state.itemGrid[sidebarEntryPath].variables.filter?.withDeleted ?? false,
  );

  const columnDefsAdjusted = useMemo<ColDef[]>(() => {
    if (withDeleted) {
      return [...columnDefs, createColDefCheckmark("deleted", "Deleted")];
    }

    return columnDefs;
  }, [columnDefs, withDeleted]);
  const totalItems = useMemo<number>(
    () => data?.total?.count ?? 0,
    [data?.total?.count],
  );

  const onGetRowId = useCallback((row: ItemGridDataRow) => row.id ?? "", []);
  const onIncludeDeletedChanged = useCallback(
    (include: boolean) =>
      dispatch(
        setItemGridWithDeleted({
          path: sidebarEntryPath,
          allPaths: true,
          withDeleted: include,
        }),
      ),
    [dispatch, sidebarEntryPath],
  );
  const onFilterChanged = useCallback(
    (filter: AnyApiGridQueryStateValue["variables"]["filter"]) =>
      dispatch(setItemGridFilter({ path: sidebarEntryPath, filter })),
    [dispatch, sidebarEntryPath],
  );
  const onFilterOpenChanged = useCallback(
    (open: boolean) =>
      dispatch(setItemGridFilterOpen({ path: sidebarEntryPath, open })),
    [dispatch, sidebarEntryPath],
  );
  const onPageChanged = useCallback(
    (p: number) =>
      dispatch(setItemGridPage({ path: sidebarEntryPath, page: p })),
    [dispatch, sidebarEntryPath],
  );
  const onPerPageChanged = useCallback(
    (p: number) =>
      dispatch(setItemGridPerPage({ path: sidebarEntryPath, perPage: p })),
    [dispatch, sidebarEntryPath],
  );
  const onQueryChanged = useCallback(
    (query?: string) =>
      dispatch(setItemGridQuery({ path: sidebarEntryPath, query })),
    [dispatch, sidebarEntryPath],
  );
  const onSelectedItemIdChanged = useCallback(
    (id?: string) =>
      dispatch(setItemGridSelectedItemId({ path: sidebarEntryPath, id })),
    [dispatch, sidebarEntryPath],
  );
  const onSortFieldChanged = useCallback(
    (field: string) =>
      dispatch(setItemGridSortField({ path: sidebarEntryPath, field })),
    [dispatch, sidebarEntryPath],
  );
  const onSortOrderChanged = useCallback(
    (order: ItemGridSortOrder) =>
      dispatch(setItemGridSortOrder({ path: sidebarEntryPath, order })),
    [dispatch, sidebarEntryPath],
  );

  return (
    <ItemGridGeneric<ItemGridDataRow>
      columnDefs={columnDefsAdjusted}
      data={data?.items}
      dataError={error}
      dataLoading={loading}
      filter={filter}
      filterPanels={filterPanels}
      filtersSupported={filtersSupported}
      filtersOpen={filtersOpen}
      includeDeleted={withDeleted}
      pagination={{
        ...pagination,
        page,
        perPage,
        totalItems,
        onPageChanged,
        onPerPageChanged,
      }}
      onGetRowId={onGetRowId}
      onFilterChanged={onFilterChanged}
      onFilterOpenChanged={onFilterOpenChanged}
      onIncludeDeletedChanged={onIncludeDeletedChanged}
      onQueryChanged={onQueryChanged}
      onSelectedItemIdChanged={onSelectedItemIdChanged}
      onSortFieldChanged={onSortFieldChanged}
      onSortOrderChanged={onSortOrderChanged}
      query={q}
    />
  );
}
